/**
 * Checklists Oficiais de Revisão Periódica: Honda ADV 150 (2023/2024 - K0W)
 */

export interface ChecklistItem {
  id: string;
  task: string;
  category: "motor" | "cvt" | "chassi_freios" | "eletrica";
  torqueRefId?: string;
  toolRefId?: string;
  moduleRef?: string;
  mandatoryNote: string;
}

export interface MaintenancePlan {
  km: number;
  title: string;
  description: string;
  items: ChecklistItem[];
}

export const ADV150_MAINTENANCE_PLANS: MaintenancePlan[] = [
  {
    km: 1000,
    title: "Revisão Inaugural (1.000 km)",
    description:
      "Assentamento de anéis, eliminação de limalhas iniciais e verificação de torques estruturais.",
    items: [
      {
        id: "rev1k-oleo",
        task: "Trocar óleo do motor eSP (0,8 L SAE 10W-30 JASO MB)",
        category: "motor",
        torqueRefId: "t-bujão-oleo-motor",
        toolRefId: "tool-torquimetro-iso6789",
        moduleRef: "Módulo 1A",
        mandatoryNote:
          "Substituir arruela de esmagamento de alumínio 12 mm OEM 94109-12000; aperto a 24 N.m (2,4 kgf.m). Nível na vareta sem rosquear.",
      },
      {
        id: "rev1k-tela-oleo",
        task: "Limpeza inaugural preventiva da tela cilíndrica de óleo (Strainer Screen 15421-KPL-900)",
        category: "motor",
        torqueRefId: "t-tampa-tela-oleo",
        toolRefId: "tool-torquimetro-iso6789",
        moduleRef: "Módulo 1A",
        mandatoryNote:
          "Remover limalhas de assentamento do bloco; inspecionar O-ring 91302-001-020 e apertar tampa M30 a 20 N.m.",
      },
      {
        id: "rev1k-marcha-lenta",
        task: "Inspecionar rotação de marcha lenta e resposta do acelerador PGM-FI",
        category: "motor",
        moduleRef: "Módulo 4B",
        mandatoryNote:
          "Padrão de fábrica: 1.700 ± 100 rpm com motor aquecido (> 80°C) dosado pela IACV.",
      },
      {
        id: "rev1k-chassi",
        task: "Verificar reaperto de porcas de eixo, fixação do cáliper e mesa do garfo Showa",
        category: "chassi_freios",
        torqueRefId: "t-eixo-dianteiro",
        toolRefId: "tool-torquimetro-iso6789",
        moduleRef: "Módulo 3B",
        mandatoryNote:
          "Garantir torques de 59 N.m (eixo dianteiro), 118 N.m (traseiro), 30 N.m (cáliper dianteiro) e 27 N.m (mesa inferior Showa).",
      },
      {
        id: "rev1k-reset-painel",
        task: "Reinicializar indicador luminoso OIL CHANGE no painel de instrumentos digital LCD",
        category: "eletrica",
        moduleRef: "Módulo 1A",
        mandatoryNote:
          "Pressionar botão SET por 3 a 5 segundos com a chave girada de OFF para ON até apagar o alerta.",
      },
    ],
  },
  {
    km: 6000,
    title: "Revisão Preventiva (6.000 km)",
    description:
      "Troca de lubrificante, dreno de condensado, inspeção de vela e testes dinâmicos de segurança.",
    items: [
      {
        id: "rev6k-oleo",
        task: "Trocar óleo do motor eSP (0,8 L SAE 10W-30 JASO MB)",
        category: "motor",
        torqueRefId: "t-bujão-oleo-motor",
        toolRefId: "tool-torquimetro-iso6789",
        moduleRef: "Módulo 1A",
        mandatoryNote:
          "Arruela nova 12 mm OEM 94109-12000; torque 24 N.m. Óleo com aditivação antifricção JASO MB (proibido JASO MA/MA2).",
      },
      {
        id: "rev6k-respiro",
        task: "Drenar resíduo de condensado e borra do tubo de respiro da caixa de ar",
        category: "motor",
        moduleRef: "Módulo 1B",
        mandatoryNote:
          "Esgotar água e borra condensada na bacia; recolocar o bujão transparente com presilha.",
      },
      {
        id: "rev6k-vela",
        task: "Inspecionar coloração da vela NGK MR8K-9 e gap do eletrodo (0,80 a 0,90 mm)",
        category: "motor",
        torqueRefId: "t-vela-ignicao",
        toolRefId: "tool-calibre-laminas",
        moduleRef: "Módulo 4A",
        mandatoryNote:
          "Chave 16 mm com borracha; calibrar gap com cálibre; rosquear à mão e apertar a 16 N.m.",
      },
      {
        id: "rev6k-pastilhas",
        task: "Medir espessura do material de atrito das pastilhas Nissin com paquímetro",
        category: "chassi_freios",
        toolRefId: "tool-paquimetro-centesimal",
        moduleRef: "Módulo 3B",
        mandatoryNote:
          "Limite de descarte 1,0 mm (ou ranhura visível). Descontaminar pistões com álcool isopropílico antes de qualquer recuo.",
      },
      {
        id: "rev6k-pneus",
        task: "Calibrar pneus a frio e inspecionar profundidade dos sulcos (TWI)",
        category: "chassi_freios",
        moduleRef: "Módulo 1B",
        mandatoryNote:
          "Pressão a frio: 29 psi dianteiro / 33 psi traseiro (solo) ou 36 psi (garupa). TWI mín 0,8 mm.",
      },
      {
        id: "rev6k-bateria",
        task: "Testar tensão em repouso da bateria YTZ6V e acionamento do Idling Stop",
        category: "eletrica",
        moduleRef: "Módulo 1B",
        mandatoryNote:
          "Tensão de repouso ≥ 12,8V (se < 12,4V: recarga lenta a 0,5A) e ≥ 10,2V na partida.",
      },
      {
        id: "rev6k-reset-painel",
        task: "Resetar aviso OIL CHANGE no visor digital LCD",
        category: "eletrica",
        moduleRef: "Módulo 1A",
        mandatoryNote: "Pressionar botão SET por 3 a 5 s com ignição em ON até o indicador apagar.",
      },
    ],
  },
  {
    km: 12000,
    title: "Revisão Completa & Inspeção CVT (12.000 km)",
    description:
      "Limpeza de tela de óleo, abertura cirúrgica do CVT e medições centesimais na primária e secundária.",
    items: [
      {
        id: "rev12k-oleo-tela",
        task: "Trocar óleo do motor e higienizar tela cilíndrica de óleo (Strainer Screen 15421-KPL-900)",
        category: "motor",
        torqueRefId: "t-tampa-tela-oleo",
        toolRefId: "tool-torquimetro-iso6789",
        moduleRef: "Módulo 1A",
        mandatoryNote:
          "Lavar malha com querosene/isopropanol e soprar de dentro para fora; O-ring 91302-001-020 novo; torque a 20 N.m.",
      },
      {
        id: "rev12k-cvt-limpeza",
        task: "Desmontar carcaça do CVT com gabarito de papelão e higienizar com álcool isopropílico",
        category: "cvt",
        torqueRefId: "t-tampa-cvt",
        moduleRef: "Módulo 2A",
        mandatoryNote:
          "Separar parafusos de 35 mm e 40 mm no gabarito; descolar com martelo de nylon (proibido fendas como alavanca); recolher os 2 pinos guia.",
      },
      {
        id: "rev12k-correia-medir",
        task: "Aferir largura útil da correia V-Belt Bando (23100-K97-T01) em 4 pontos com paquímetro",
        category: "cvt",
        toolRefId: "tool-paquimetro-centesimal",
        moduleRef: "Módulo 2A",
        mandatoryNote:
          "Padrão: 19,5 mm; limite de descarte: 18,5 mm. Executar teste de flexão reversa inspecionando trincas na raiz dos dentes.",
      },
      {
        id: "rev12k-roletes-medir",
        task: "Medir diâmetro externo dos 6 roletes centrífugos (22123-K0W-B00, 21g cada)",
        category: "cvt",
        toolRefId: "tool-paquimetro-centesimal",
        moduleRef: "Módulo 2A",
        mandatoryNote:
          "Padrão: 20,0 mm; limite: 19,5 mm. Rejeição imediata se houver facetas planas ('roletes quadrados'). Operação 100% A SECO.",
      },
      {
        id: "rev12k-guias",
        task: "Inspecionar os 3 cursores plásticos guia (slide pieces 22011-K97-T00) na rampa",
        category: "cvt",
        moduleRef: "Módulo 2A",
        mandatoryNote:
          "Eliminar folga lateral circunferencial na placa de rampa para sanar o ruído de chocalho na marcha lenta.",
      },
      {
        id: "rev12k-campana-sapatas",
        task: "Metrologia do diâmetro interno da campana (125 mm) e espessura das lonas de embreagem",
        category: "cvt",
        toolRefId: "tool-paquimetro-centesimal",
        moduleRef: "Módulo 2B",
        mandatoryNote:
          "Campana: máx 125,5 mm; Lonas: mín 2,0 mm. Desvitrificar lonas com lixa d'água 220 em movimento cruzado.",
      },
      {
        id: "rev12k-filtro-cvt",
        task: "Lavar elemento de espuma do duto do CVT (17274-K0W-N00) com água e sabão neutro",
        category: "cvt",
        moduleRef: "Módulo 2A",
        mandatoryNote: "Secar 100% à sombra antes de reinstalar. Proibido embeber em óleo.",
      },
      {
        id: "rev12k-torques-cvt",
        task: "Montagem do CVT com afundamento de correia (belt sinking) e torquímetro",
        category: "cvt",
        torqueRefId: "t-polia-primaria",
        toolRefId: "tool-fixador-y",
        moduleRef: "Módulo 2B",
        mandatoryNote:
          "Afundar correia na secundária para não prensar na primária (metal-metal); porca primária M14 a 59 N.m com chave em Y; campana M12 a 49 N.m; tampa em cruz a 10 N.m.",
      },
    ],
  },
  {
    km: 18000,
    title: "Revisão Intermediária: Filtro de Ar, Vela & Válvulas (18.000 km)",
    description:
      "Substituição de consumíveis vitais, regulagem de válvulas a frio, higienização do TBI e reset DLC.",
    items: [
      {
        id: "rev18k-oleo",
        task: "Trocar óleo do motor eSP (0,8 L SAE 10W-30 JASO MB)",
        category: "motor",
        torqueRefId: "t-bujão-oleo-motor",
        toolRefId: "tool-torquimetro-iso6789",
        moduleRef: "Módulo 1A",
        mandatoryNote: "Arruela nova 12 mm OEM 94109-12000; aperto torquimétrico a 24 N.m.",
      },
      {
        id: "rev18k-filtro-ar",
        task: "Substituir elemento do filtro de ar do motor (17210-K0W-B00)",
        category: "motor",
        moduleRef: "Módulo 1B",
        mandatoryNote:
          "Elemento viscoso novo genuíno. NUNCA limpar com ar comprimido ou solventes (destrói a retenção).",
      },
      {
        id: "rev18k-vela-trocar",
        task: "Substituir vela de ignição por nova genuína NGK MR8K-9 (31918-K97-T01)",
        category: "motor",
        torqueRefId: "t-vela-ignicao",
        toolRefId: "tool-chave-vela-16mm",
        moduleRef: "Módulo 4A",
        mandatoryNote:
          "Calibrar gap para 0,80 a 0,90 mm; soprar poço antes da remoção; rosquear à mão e apertar a 16 N.m.",
      },
      {
        id: "rev18k-valvulas",
        task: "Regular folga de válvulas com motor rigorosamente a frio (< 35°C) no PMS de compressão",
        category: "motor",
        torqueRefId: "t-contraporca-valvula",
        toolRefId: "tool-calibre-laminas",
        moduleRef: "Módulo 4A",
        mandatoryNote:
          "Admissão: 0,16 ± 0,02 mm (ou 0,10 mm padrão K0W); Escape: 0,25 ± 0,02 mm (ou 0,24 mm). Contraporca M5 a 10 N.m imobilizando o parafuso quadrado para evitar line drift.",
      },
      {
        id: "rev18k-tbi",
        task: "Higienização técnica do corpo de borboleta Keihin 26 mm com álcool isopropílico",
        category: "motor",
        torqueRefId: "t-coletor-tbi",
        moduleRef: "Módulo 4B",
        mandatoryNote:
          "Preservar intacta a película selante preta de MoS2 ao redor da borboleta. Proibido usar estiletes, thinner ou Car 80.",
      },
      {
        id: "rev18k-reset-ecm",
        task: "Limpeza de histórico de falhas da EEPROM e calibração de repouso do sensor TP via DLC",
        category: "eletrica",
        toolRefId: "tool-jumper-scs",
        moduleRef: "Módulo 4B",
        mandatoryNote:
          "Jumper SCS no DLC vermelho (fio Marrom ao Verde/Preto). Reset TP com jumper temporário no sensor EOT removido em menos de 10 s.",
      },
      {
        id: "rev18k-aprendizado-lenta",
        task: "Executar ciclo de 10 minutos de aprendizado da marcha lenta (1.700 ± 100 rpm)",
        category: "eletrica",
        moduleRef: "Módulo 4B",
        mandatoryNote:
          "Moto no cavalete central por 10 min em lenta sem tocar no acelerador até estabilizar a temperatura > 80°C.",
      },
    ],
  },
  {
    km: 24000,
    title: "Grande Revisão Estrutural: Correia, Fluido de Freio & Redutor (24.000 km)",
    description:
      "Troca total de correia, roletes, fluido de freio DOT 4 por deslocamento, reengraxamento do torque cam e óleo da transmissão final.",
    items: [
      {
        id: "rev24k-oleo-motor-redutor",
        task: "Trocar óleo do motor (0,8 L) e óleo da transmissão final traseira (120 ml)",
        category: "motor",
        torqueRefId: "t-bujão-redutor-final",
        toolRefId: "tool-torquimetro-iso6789",
        moduleRef: "Módulo 1A",
        mandatoryNote:
          "Abrir bujão superior antes do dreno; dosar 120 ml com seringa e mangueira; 2 arruelas novas 8 mm OEM 90495-MN5-000 a 13 N.m.",
      },
      {
        id: "rev24k-correia-trocar",
        task: "Substituir correia dentada de transmissão por nova genuína (23100-K97-T01)",
        category: "cvt",
        moduleRef: "Módulo 2A",
        mandatoryNote:
          "Troca compulsória preventiva de fábrica. Respeitar sentido de rotação e código legível voltado para o operador.",
      },
      {
        id: "rev24k-roletes-trocar",
        task: "Substituir jogo completo de 6 roletes (22123-K0W-B00, 21g) e 3 cursores da rampa",
        category: "cvt",
        moduleRef: "Módulo 2A",
        mandatoryNote:
          "Instalação 100% a seco nas pistas do variador após limpeza com álcool isopropílico. Proibido graxa.",
      },
      {
        id: "rev24k-polia-secundaria",
        task: "Desmontar polia secundária, reengraxar torque cam com graxa de poliureia com MoS2 e trocar retentores",
        category: "cvt",
        torqueRefId: "t-porca-mola-secundaria",
        toolRefId: "tool-compressor-mola",
        moduleRef: "Módulo 2B",
        mandatoryNote:
          "Compressor 07LME-GZ40201 obrigatório contra risco balístico (35-50 kgf); porca 39 mm com chave plana 07GMA-KS40100 a 54 N.m; graxa especial ponto de gota > 260°C.",
      },
      {
        id: "rev24k-fluido-freio",
        task: "Substituir 100% do fluido de freio DOT 4 nos circuitos dianteiro e traseiro",
        category: "chassi_freios",
        torqueRefId: "t-purga-sangria-freio",
        toolRefId: "tool-jis-vessel",
        moduleRef: "Módulo 3B",
        mandatoryNote:
          "Abrir reservatório com chave JIS #2; regra dos 30% para blindar o modulador ABS contra ar; sangria em 4 tempos; válvula a 5,4 N.m.",
      },
      {
        id: "rev24k-suspensao-inspecionar",
        task: "Inspecionar garfos Showa 31 mm (curso 130 mm) e amortecedores traseiros Showa Piggyback",
        category: "chassi_freios",
        torqueRefId: "t-amortecedor-traseiro-sup",
        moduleRef: "Módulo 3B",
        mandatoryNote:
          "Verificar retentores de bengala contra vazamento de óleo e buchas de borracha dos olhais Showa (40 N.m superior / 27 N.m inferior).",
      },
    ],
  },
  {
    km: 36000,
    title: "Revisão Profunda: Sistema de Arrefecimento (36.000 km)",
    description:
      "Substituição total do líquido de arrefecimento, protocolo de purga e sangria de bolhas e teste térmico.",
    items: [
      {
        id: "rev36k-coolant",
        task: "Drenar e substituir 100% do líquido de arrefecimento (Honda Pro Coolant Tipo 2 08CLA-G01-020)",
        category: "motor",
        torqueRefId: "t-dreno-coolant",
        toolRefId: "tool-torquimetro-iso6789",
        moduleRef: "Módulo 3A",
        mandatoryNote:
          "Dreno na bomba d'água com arruela de cobre nova 6,5 mm 90441-ME9-000 a 10 N.m; fluido OAT 50/50 sem silicatos para blindar selo mecânico.",
      },
      {
        id: "rev36k-purga-sangria",
        task: "Executar protocolo de purga e sangria de bolhas de ar no radiador com acelerações controladas",
        category: "motor",
        moduleRef: "Módulo 3A",
        mandatoryNote:
          "Funcionar 3 a 5 min em lenta com tampa aberta até abrir a termostática (82°C); 2 a 3 golpes a 2.500 rpm; completar menisco e fechar tampa a 108 kPa.",
      },
      {
        id: "rev36k-expansao",
        task: "Limpar e calibrar o nível do reservatório de expansão até a linha UPPER",
        category: "motor",
        moduleRef: "Módulo 3A",
        mandatoryNote:
          "Capacidade total ~0,61 L (480 ml radiador + 130 ml expansão). Vistoriar duto testemunha (weep hole) contra vazamentos.",
      },
    ],
  },
];
